import { useEffect, useState } from 'react';
import { ICheckoutPage } from '../../../../api/checkouts/types/checkout-page.type';

/**
 * Ponte de PRÉ-VISUALIZAÇÃO do builder.
 *
 * Com `?_preview=1`, o checkout passa a desenhar a página que o editor manda
 * por `postMessage` — o rascunho, sem precisar salvar. É o que permite ter um
 * render só: o preview do builder É esta página, não uma réplica.
 *
 * Segurança: o modo é opt-in pela própria URL e só aceita mensagens de
 * origens conhecidas (a plataforma, o painel Waid e localhost em dev).
 * Fora do modo preview nada é escutado.
 */

export const PREVIEW_MESSAGE = 'waid-checkout-preview';
export const PREVIEW_READY = 'waid-checkout-preview-ready';
/** Editor → preview: começar edição inline de texto num bloco. */
export const PREVIEW_EDIT_START = 'waid-checkout-edit-start';
/** Preview → editor: texto editado inline (blur/Enter). */
export const PREVIEW_EDIT = 'waid-checkout-preview-edit';

const isTrustedOrigin = (origin: string): boolean => {
	try {
		const { hostname, protocol } = new URL(origin);
		if (protocol !== 'https:' && protocol !== 'http:') return false;
		if (hostname === 'localhost' || hostname === '127.0.0.1') {
			return process.env.NODE_ENV !== 'production';
		}
		return (
			hostname.endsWith('.curseduca.pro') ||
			hostname.endsWith('.curseduca.com') ||
			hostname.endsWith('.waid.io') ||
			// As plataformas novas vivem em `{slug}.waid.pro`. Sem este
			// domínio o rascunho inteiro era descartado: o preview do builder
			// mostrava só a página publicada, e trocar o tema ou adicionar um
			// bloco não refletia até publicar.
			hostname.endsWith('.waid.pro')
		);
	} catch {
		return false;
	}
};

export const isPreviewMode = (): boolean => {
	if (typeof window === 'undefined') return false;
	return new URLSearchParams(window.location.search).get('_preview') === '1';
};

/**
 * Edição inline (duplo clique no editor): torna o texto do bloco editável no
 * próprio preview e devolve o resultado no blur/Enter. Só título e texto —
 * o conteúdo é o innerText, sem HTML.
 */
const startInlineEdit = (blockId: string) => {
	const wrapper = document.querySelector<HTMLElement>(
		`[data-block-id="${blockId}"]`
	);
	if (!wrapper) return;
	const target =
		wrapper.querySelector<HTMLElement>(
			'.page-block-title, .page-block-text'
		) ??
		(wrapper.classList.contains('page-block-title') ||
		wrapper.classList.contains('page-block-text')
			? wrapper
			: null);
	if (!target || target.isContentEditable) return;

	const original = target.innerText;
	target.contentEditable = 'true';
	target.style.outline = '2px dashed rgba(124,58,237,0.6)';
	target.style.outlineOffset = '4px';
	target.style.cursor = 'text';
	target.focus();
	// Caret no fim do texto (comportamento esperado de "continuar digitando").
	try {
		const range = document.createRange();
		range.selectNodeContents(target);
		range.collapse(false);
		const selection = window.getSelection();
		selection?.removeAllRanges();
		selection?.addRange(range);
	} catch {
		// sem seleção programática, o clique do usuário posiciona o caret
	}

	const finish = (commit: boolean) => {
		target.contentEditable = 'false';
		target.style.outline = '';
		target.style.outlineOffset = '';
		target.style.cursor = '';
		target.removeEventListener('blur', onBlur);
		target.removeEventListener('keydown', onKeyDown);
		const text = target.innerText.trim();
		if (!commit || text === original.trim()) {
			target.innerText = original;
			window.parent?.postMessage(
				{ type: PREVIEW_EDIT, blockId, cancelled: true },
				'*'
			);
			return;
		}
		window.parent?.postMessage({ type: PREVIEW_EDIT, blockId, text }, '*');
	};

	const onBlur = () => finish(true);
	const onKeyDown = (event: KeyboardEvent) => {
		if (event.key === 'Escape') {
			event.preventDefault();
			finish(false);
		}
		// Enter confirma em títulos (uma linha); em textos, Enter quebra linha
		// e Ctrl/Cmd+Enter confirma.
		const isTitle = target.classList.contains('page-block-title');
		if (
			event.key === 'Enter' &&
			(isTitle || event.metaKey || event.ctrlKey)
		) {
			event.preventDefault();
			target.blur();
		}
	};
	target.addEventListener('blur', onBlur);
	target.addEventListener('keydown', onKeyDown);
};

/**
 * Página vinda do editor. `undefined` enquanto o editor não mandou nada —
 * o caller segue com o que veio da API.
 */
export const usePreviewBridge = (): ICheckoutPage | undefined => {
	const [page, setPage] = useState<ICheckoutPage | undefined>();

	useEffect(() => {
		if (!isPreviewMode()) return;

		const onMessage = (event: MessageEvent) => {
			if (!isTrustedOrigin(event.origin)) return;
			const data = event.data;
			if (data?.type === PREVIEW_EDIT_START && data.blockId) {
				startInlineEdit(String(data.blockId));
				return;
			}
			if (!data || data.type !== PREVIEW_MESSAGE) return;
			setPage(data.page as ICheckoutPage);
		};

		window.addEventListener('message', onMessage);

		// Avisa o editor que já pode mandar o rascunho (evita corrida entre o
		// carregamento do iframe e o primeiro envio).
		window.parent?.postMessage({ type: PREVIEW_READY }, '*');

		return () => window.removeEventListener('message', onMessage);
	}, []);

	return page;
};

export const PREVIEW_LAYOUT = 'waid-checkout-preview-layout';

export type PreviewBlockRect = {
	id: string;
	top: number;
	left: number;
	width: number;
	height: number;
};

/**
 * Reporta ao editor onde cada bloco caiu na página real, para ele desenhar
 * os controles (seleção, arrastar, remover) por cima do iframe — o canvas de
 * edição passa a ser o próprio checkout.
 *
 * Reage a qualquer mudança de layout (ResizeObserver + MutationObserver),
 * porque o conteúdo muda de altura conforme o comprador interage (abrir
 * cupom, trocar meio de pagamento) e o overlay precisa acompanhar.
 */
export const usePreviewLayoutReporter = (enabled: boolean) => {
	useEffect(() => {
		if (!enabled || !isPreviewMode()) return;

		let frame = 0;

		const report = () => {
			cancelAnimationFrame(frame);
			frame = requestAnimationFrame(() => {
				const nodes = Array.from(
					document.querySelectorAll<HTMLElement>('[data-block-id]')
				);
				const blocks: PreviewBlockRect[] = nodes.map((node) => {
					const rect = node.getBoundingClientRect();
					return {
						id: node.dataset.blockId as string,
						top: rect.top + window.scrollY,
						left: rect.left + window.scrollX,
						width: rect.width,
						height: rect.height
					};
				});

				/**
				 * Áreas dos containers: o editor precisa delas para aceitar
				 * uma solta no fim da página ou numa coluna lateral vazia,
				 * onde não há bloco (e portanto nenhum retângulo) para mirar.
				 */
				const containerRect = (selector: string) => {
					const el = document.querySelector<HTMLElement>(selector);
					if (!el) return null;
					const rect = el.getBoundingClientRect();
					return {
						top: rect.top + window.scrollY,
						left: rect.left + window.scrollX,
						width: rect.width,
						height: rect.height
					};
				};

				window.parent?.postMessage(
					{
						type: PREVIEW_LAYOUT,
						blocks,
						containers: {
							main: containerRect(
								'.page-blocks:not(.page-blocks-aside)'
							),
							aside: containerRect('.sidebar-grid-item'),
							form: containerRect('.form-grid-item'),
							submit: containerRect('.checkout-submit-area'),
							footer: containerRect('.page-blocks-footer'),
							grid: containerRect('.checkout-grid'),
							buyButton: containerRect(
								'.checkout-submit-area button[type="submit"]'
							),
							thanksHero: containerRect('.thanks-hero'),
							summaryLogo: containerRect(
								'.sidebar-grid-item .card-header img'
							)
						},
						scrollY: window.scrollY,
						docHeight: document.documentElement.scrollHeight
					},
					'*'
				);
			});
		};

		report();

		const resizeObserver = new ResizeObserver(report);
		resizeObserver.observe(document.body);

		const mutationObserver = new MutationObserver(report);
		mutationObserver.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true
		});

		window.addEventListener('scroll', report, { passive: true });
		window.addEventListener('resize', report);

		return () => {
			cancelAnimationFrame(frame);
			resizeObserver.disconnect();
			mutationObserver.disconnect();
			window.removeEventListener('scroll', report);
			window.removeEventListener('resize', report);
		};
	}, [enabled]);
};
